import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, ShieldCheck, KeyRound } from 'lucide-react';
import { erpList, erpCreate } from '../../../services/erpApi';
import { Card, CardHeader, Button, Input, Select, PageHeader, Table, FormModal, EmptyState, Badge, fmtDate, initials } from '../ui';

const EMPTY = { name: '', email: '', password: '', role: 'sales' };

export const UsersPage = () => {
  const [data, setData] = useState({ users: [], roles: [], all_permissions: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await erpList('users'); setData(r.data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Only admins can manage users'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await erpCreate('users', form); toast.success('User created'); setModal(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const columns = [
    { key: 'name', label: 'User', render: (u) => (
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full grid place-items-center text-xs font-bold text-white" style={{ background: 'var(--erp-navy)' }}>{initials(u.name)}</span>
        <div><p className="font-semibold">{u.name}</p><p className="text-xs" style={{ color: 'var(--erp-muted)' }}>{u.email}</p></div>
      </div>) },
    { key: 'role', label: 'Role', render: (u) => <Badge tone={u.role === 'admin' ? 'navy' : u.role === 'restricted' ? 'amber' : 'blue'} className="capitalize">{u.role}</Badge> },
    { key: 'permissions', label: 'Permissions', render: (u) => (
      <div className="flex flex-wrap gap-1 max-w-md">
        {(u.permissions || []).slice(0, 4).map((p) => <span key={p} className="text-[11px] px-2 py-0.5 rounded-md" style={{ background: '#f3f6fb', color: '#3b4664' }}>{p.replace(/_/g, ' ')}</span>)}
        {(u.permissions || []).length > 4 && <span className="text-[11px] px-2 py-0.5 font-semibold" style={{ color: 'var(--erp-muted)' }}>+{u.permissions.length - 4} more</span>}
      </div>) },
    { key: 'is_active', label: 'Status', render: (u) => <Badge tone={u.is_active === false ? 'gray' : 'green'}>{u.is_active === false ? 'Inactive' : 'Active'}</Badge> },
    { key: 'created_at', label: 'Created', render: (u) => <span style={{ color: 'var(--erp-muted)' }}>{fmtDate(u.created_at)}</span> },
  ];

  return (
    <div data-testid="erp-users">
      <PageHeader crumb="Administration" title="Users & Roles" subtitle="Role-based access. Restricted roles never see customer or vendor identities."
        actions={<Button icon={Plus} onClick={() => setModal(true)} data-testid="user-add">New User</Button>} />
      <div className="grid xl:grid-cols-[1fr_320px] gap-4 items-start">
        <Card className="p-0 erp-rise erp-d1">
          <Table testid="user-table" columns={columns} rows={data.users} loading={loading} empty={<EmptyState icon={ShieldCheck} title="No users" />} />
        </Card>
        <Card className="p-0 erp-rise erp-d2">
          <CardHeader icon={KeyRound} title="Roles" subtitle="Available role templates" />
          <ul className="px-5 pb-5 space-y-2">
            {data.roles.map((r) => <li key={r} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg" style={{ background: '#f8fafd' }}><span className="font-semibold capitalize">{r}</span><span className="text-xs" style={{ color: 'var(--erp-muted)' }}>{data.users.filter((u) => u.role === r).length} users</span></li>)}
          </ul>
        </Card>
      </div>

      <FormModal open={modal} onClose={() => setModal(false)} onSubmit={save} busy={busy} testid="user-modal" submitLabel="Create User" title="New user" subtitle="Credentials are hashed; share the password securely.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Full name" required className="sm:col-span-2" value={form.name} onChange={set('name')} placeholder="Ravi Singh" data-testid="user-name" />
          <Input label="Work email" type="email" required value={form.email} onChange={set('email')} placeholder="ravi@namohtoolings.in" data-testid="user-email" />
          <Input label="Password" type="password" required minLength={6} value={form.password} onChange={set('password')} placeholder="Min 6 characters" data-testid="user-password" />
          <Select label="Role" className="sm:col-span-2" value={form.role} onChange={set('role')} data-testid="user-role">
            {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        </div>
      </FormModal>
    </div>
  );
};
